// All Firestore access for the school module goes through this file — same
// principle as lib/firebase.js for the rest of Grove: components import from
// here, never from 'firebase/firestore' directly. Ported from school-site's
// src/data/firestore.ts, with two deliberate changes to match Grove's
// conventions instead of school-site's:
//   - keyed by studentEmail (a children/{id}.studentEmail value) instead of
//     a Firebase Auth uid, matching firestore.rules' isHomeschoolStudent()
//   - fsSet already stamps updatedAt via serverTimestamp on every write
//     (see lib/firebase.js), so callers here don't set it themselves

import { collection, doc, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, fsGet, fsSet, fsGetCol, fsListenCol, fsListen } from '../lib/firebase';
import { stripUndefined } from './calendarMath';

const coursesPath      = familyId => `families/${familyId}/courses`;
const assignmentsPath  = familyId => `families/${familyId}/assignments`;
const attendancePath   = familyId => `families/${familyId}/attendanceLog`;
const importBatchesPath = familyId => `families/${familyId}/importBatches`;

// ─── COURSES ──────────────────────────────────────────────────────────────────

export function listenCourses(familyId, cb) {
  return fsListenCol(coursesPath(familyId), [], cb);
}

export async function fetchCoursesOnce(familyId) {
  return fsGetCol(coursesPath(familyId));
}

export async function upsertCourse(familyId, course) {
  await fsSet(`${coursesPath(familyId)}/${course.id}`, course);
}

// Batched — the Import tab can hand this several new courses (discovered
// from a CSV's "course" column) in one commit, same as upsertAssignments.
export async function upsertCourses(familyId, courses) {
  if (courses.length === 0) return;
  const col = collection(db, 'families', familyId, 'courses');
  const batch = writeBatch(db);
  for (const c of courses) batch.set(doc(col, c.id), c, { merge: true });
  await batch.commit();
}

// ─── ASSIGNMENTS ──────────────────────────────────────────────────────────────
// Live-listened (not fetch-once) so a parent's grading/feedback shows up on
// the student's screen without a refresh, and vice versa — the one place in
// the school module where that cross-device sync genuinely matters.

export function listenAssignments(familyId, studentEmail, cb) {
  return fsListenCol(
    assignmentsPath(familyId),
    [{ field: 'studentEmail', op: '==', value: studentEmail }],
    cb
  );
}

export async function fetchAllAssignmentsOnce(familyId, studentEmail) {
  return fsGetCol(assignmentsPath(familyId), [{ field: 'studentEmail', op: '==', value: studentEmail }]);
}

// Fields a student may touch on their own assignment — see
// STUDENT_WRITABLE_FIELDS in schema.js and the matching rule in
// firestore.rules' studentEditableKeys().
export async function updateAssignmentAsStudent(familyId, assignmentId, patch) {
  await fsSet(`${assignmentsPath(familyId)}/${assignmentId}`, patch);
}

export async function updateAssignmentAsOwner(familyId, assignmentId, patch) {
  await fsSet(`${assignmentsPath(familyId)}/${assignmentId}`, patch);
}

// Places a student-placed weekly-board tile onto a specific day — the only
// write a student can make to date/dayOfWeek/order (see firestore.rules'
// placementMode guard). Re-runnable: moving an already-placed tile to another
// day is the same write, since a wrong day should be fixable.
export async function placeAssignment(familyId, assignmentId, date, dayOfWeek, order) {
  await fsSet(`${assignmentsPath(familyId)}/${assignmentId}`, { date, dayOfWeek, order });
}

// The undo half of placeAssignment — sends a tile back to the week board's
// "needs a day" pool. Explicit nulls (not undefined) so the merge write actually
// clears the fields rather than leaving the old day in place.
export async function unplaceAssignment(familyId, assignmentId) {
  await fsSet(`${assignmentsPath(familyId)}/${assignmentId}`, { date: null, dayOfWeek: null, order: null });
}

// Firestore batched writes cap at 500 ops; chunk so the seed generator and
// the CSV importer can both hand this a full year's worth of rows. Goes
// straight to the SDK (not fsSet) because fsSet doesn't batch and this needs
// to stay one atomic commit per chunk, same as school-site's original.
export async function upsertAssignments(familyId, assignments) {
  const CHUNK = 400;
  const col = collection(db, 'families', familyId, 'assignments');
  for (let i = 0; i < assignments.length; i += CHUNK) {
    const chunk = assignments.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    for (const a of chunk) batch.set(doc(col, a.id), stripUndefined(a), { merge: true });
    await batch.commit();
  }
}

export async function deleteAssignments(familyId, ids) {
  const CHUNK = 400;
  const col = collection(db, 'families', familyId, 'assignments');
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    for (const id of chunk) batch.delete(doc(col, id));
    await batch.commit();
  }
}

// ─── IMPORT BATCHES (audit log — owner-only, see firestore.rules) ─────────────

export async function logImportBatch(familyId, importBatch) {
  await fsSet(`${importBatchesPath(familyId)}/${importBatch.id}`, importBatch);
}

export async function fetchImportBatches(familyId) {
  const rows = await fsGetCol(importBatchesPath(familyId));
  return rows.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

export function listenImportBatches(familyId, cb) {
  return fsListenCol(importBatchesPath(familyId), [], rows =>
    cb(rows.slice().sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')))
  );
}

// ─── ATTENDANCE ───────────────────────────────────────────────────────────────

export function listenAttendance(familyId, cb) {
  return fsListenCol(attendancePath(familyId), [], cb);
}

export async function upsertAttendanceDay(familyId, day) {
  await fsSet(`${attendancePath(familyId)}/${day.date}`, day);
}

// ─── PHOTO PROOF ──────────────────────────────────────────────────────────────
// Mirrors storage.rules' families/{familyId}/assignments/{assignmentId}/{file}
// path — a student uploading their own work, or an owner uploading on their
// behalf, both allowed there.

export async function uploadAssignmentPhoto(familyId, assignmentId, file) {
  const path = `families/${familyId}/assignments/${assignmentId}/${Date.now()}-${file.name}`;
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file);
  return getDownloadURL(fileRef);
}

// ─── SCHOOL CALENDAR (piggybacks on Grove's existing config/{id} subcollection) ─

export async function fetchSchoolCalendar(familyId) {
  return fsGet(`families/${familyId}/config/schoolCalendar`);
}

export async function saveSchoolCalendar(familyId, calendar) {
  await fsSet(`families/${familyId}/config/schoolCalendar`, calendar);
}

// ─── DAILY WINDOWS ("he books you") ────────────────────────────────────────────
// Same config/{id} reuse — see schema.js.

export function listenDailyWindows(familyId, cb) {
  return fsListen(`families/${familyId}/config/dailyWindows`, cb);
}

export async function saveDailyWindows(familyId, date, windows) {
  await fsSet(`families/${familyId}/config/dailyWindows`, { date, windows });
}

// ─── TERM MILESTONES ────────────────────────────────────────────────────────────

export async function fetchMilestones(familyId) {
  return fsGet(`families/${familyId}/config/schoolMilestones`);
}

export async function saveMilestones(familyId, milestones) {
  await fsSet(`families/${familyId}/config/schoolMilestones`, milestones);
}
